// SPDX-License-Identifier: Apache-2.0

import { Connection, disconnect, promiseWithTimeout } from "../wire/connection";
import OpenChannel from "../api/OpenChannel";
import Channel from "../channel/Channel";
import Success from "../api/Success";
import SendETHTx from "../api/SendETHTx";
import ProposalResponse from "../api/ProposalResponse";
import {
  TransactionRequest,
  TransactionResponse,
} from "@ethersproject/abstract-provider";
import State from "./State";
import Proposal from "../channel/Proposal";
import GetChannelInfo from "../api/GetChannelInfo";
import ChannelInfo from "../api/ChannelInfo";
import CloseChannel from "../api/CloseChannel";
import {
  challengeDurationSeconds,
  defaultTimeoutSeconds,
  finishSwapTimeout,
} from "../constants/Constants";
import { getRejectReason, Rejected } from "../api/RejectReason";
import ChannelID from "../api/types/channelID";
import Provider, {
  CkbCCCProvider,
  convertEthSignatureToDER,
  FreighterProvider,
} from "./Provider";
import GetAssetsResponse from "../api/GetAssetsResponse";
import GetAssets from "../api/GetAssets";
import Swap, { SwapID } from "../channel/Swap";
import ChannelState, {
  Asset,
  ChannelAsset,
  ChannelNervosAsset,
  ChannelStellarAsset,
} from "../api/types/channelState";
import UpdateChannel from "../api/UpdateChannel";
import PerunError from "../api/Error";
import {
  finalizingSwapNotice,
  dispatchPerunNotice,
  revertPerunNotice,
  initiateSwapNotice,
  performingSwapNotice,
  openChannelFoundNotice,
  fundingChannelFoundNotice,
  recoveredFundsNotice,
  recoverFundingFailedNotice,
  recoverFundingRunningNotice,
  dispatchNavigateToFinish,
} from "../events/Notice";
import i18n from "i18next";
import SendETHTxResponse from "../api/SendETHTxResponse";
import GetChainsResponse from "../api/GetChainsResponse";
import GetChains from "../api/GetChains";
import GetDecimals from "../api/GetDecimals";
import GetDecimalsResponse from "../api/GetDecimalsResponse";
import GetTimeout, { TimeoutType } from "../api/GetTimeout";
import GetTimeoutResponse from "../api/GetTimeoutResponse";
import GetQuote from "../api/GetQuote";
import GetQuoteResponse from "../api/GetQuoteResponse";
import SignedState from "../api/SignedState";
import Dispose from "../api/Dispose";
import SendSteTx from "../api/SendSteTx";
import SendSteTxResponse from "../api/SendSteTxResponse";
import GetBalance from "../api/GetBalance";
import GetBalanceResponse from "../api/GetBalanceResponse";
import Address from "../api/types/address";
import Allocation, { Params, SState } from "../api/types/channelSignedState";
import GetSignedState from "../api/GetSignedState";
import SetAdjTxSender from "../api/SetAdjTxSender";
import EthereumAsset from "../api/types/ethereumAsset";
import { Mutex } from "async-mutex";
import SendNervTx from "../api/SendNervTx";
import SendNervTxResponse from "../api/SendNervTxResponse";
import SignNervData from "../api/SignNervData";
import SignResponse from "../api/SignResponse";
import { utils } from "ethers";
import { SUDT } from "../api/types/nervosAsset";
import RecoverFunds from "../api/RecoverFunds";
import RecoverFunding from "../api/RecoverFunding";
import OpenChannelFound from "../api/OpenChannelFound";
import FundingChannelFound from "../api/FundingChannelFound";
import GetGuardrails from "../api/GetGuardrails";
import GetGuardrailsResponse from "../api/GetGuardrailsResponse";

const txMutex = new Mutex();

/**
 * Represents the client which communicates with the websocket and is able to
 * create signatures using a Web3Provider.
 */
export class Client {
  public state: State;
  public initialized = false;
  public l2Address?: Address;
  private conn: Connection;
  readonly ethProvider?: Provider;
  readonly steProvider?: FreighterProvider;
  readonly ckbProvider?: CkbCCCProvider;
  readonly ethAddress?: Address;
  readonly steAddress?: string;
  readonly nervosPubKey?: string;
  readonly nervosAddress?: string;
  readonly defaultAddress: string | undefined;

  constructor(
    conn: Connection,
    ethProvider?: Provider,
    steProvider?: FreighterProvider,
    nervosProvider?: CkbCCCProvider,
    ethAddress?: string,
    steAddress?: string,
    nervosAddress?: string,
    nervosPubKey?: string,
  ) {
    if (!ethProvider && !steProvider && !nervosProvider) {
      throw new Error("at least one provider must be defined");
    }

    this.state = new State();
    this.conn = conn;
    this.ethProvider = ethProvider;
    this.steProvider = steProvider;
    this.ckbProvider = nervosProvider;
    if (ethAddress) {
      this.ethAddress = Address.fromJSON(ethAddress);
    }
    this.steAddress = steAddress;
    if (ethProvider) {
      this.defaultAddress = ethProvider.getAddress();
      console.log(
        "Default address set from Ethereum provider: ",
        this.defaultAddress,
      );
      this.ethAddress = Address.fromJSON(this.defaultAddress);
    }
    if (steProvider) {
      this.defaultAddress = steProvider.getAddress();
      this.steAddress = this.defaultAddress;
    }
    if (nervosAddress) {
      this.defaultAddress = nervosAddress;
    }
    this.nervosAddress = nervosAddress;
    this.nervosPubKey = nervosPubKey;
    console.log(
      "Client initialized with address: ",
      this.ethAddress,
      this.steAddress,
      this.defaultAddress,
    );
    this.initHandlers();
  }

  /**
   * Initializes the client on the websocket by connecting to it and sending its
   * own address.
   *
   * @returns A Promise with a boolean for whether the client has been
   * successfully initialized.
   */
  public async initializeConn(l2Account?: string): Promise<boolean> {
    if (this.initialized) {
      return Promise.reject("client is already initialized");
    }

    await this.conn.connect();
    try {
      let ethAddr = Address.zero();
      let steAddr = "";
      let pubKey = "";
      let codeHash = "0x";
      if (this.ethProvider) {
        ethAddr = Address.fromJSON(this.ethProvider.getAddress());
      }
      if (this.ethAddress) {
        ethAddr = this.ethAddress;
      }
      if (this.steProvider) {
        steAddr = this.steProvider.getAddress();
      }
      if (this.steAddress) {
        steAddr = this.steAddress;
      }
      if (this.ckbProvider && this.nervosPubKey) {
        console.log("CKB Provider connected");
        pubKey = this.nervosPubKey;
        codeHash = await this.ckbProvider.getCodeHash();
      }

      const initialized = await promiseWithTimeout(
        defaultTimeoutSeconds,
        this.conn.initialize(ethAddr, steAddr, codeHash, pubKey, l2Account),
      );
      if (initialized?.l2Address) {
        this.l2Address = initialized.l2Address;
      }
    } catch (err) {
      console.error("Error", err);
      return false;
    }
    this.initialized = true;
    return this.initialized;
  }

  /**
   * Initializes the client on the websocket by connecting to it and sending its
   * own address.
   *
   * @returns A Promise with a boolean for whether the client has been
   * successfully initialized.
   */
  public async finishSwap(signedState: SignedState): Promise<boolean> {
    if (this.initialized) {
      return Promise.reject("client is already initialized");
    }

    await this.conn.connect();
    try {
      let ethAddr = Address.zero();
      let steAddr = "";
      let pubKey = "";
      let codeHash = "0x";
      if (this.ethProvider) {
        ethAddr = Address.fromJSON(this.ethProvider.getAddress());
      }
      if (this.ethAddress) {
        ethAddr = this.ethAddress;
      }
      if (this.steProvider) {
        steAddr = this.steProvider.getAddress();
      }
      if (this.steAddress) {
        steAddr = this.steAddress;
      }
      if (this.ckbProvider && this.nervosPubKey) {
        console.log("CKB Provider connected");
        pubKey = this.nervosPubKey;
        codeHash = await this.ckbProvider.getCodeHash();
      }
      console.log("Initialize with: ", ethAddr, steAddr, codeHash, pubKey);
      await promiseWithTimeout(
        finishSwapTimeout,
        this.conn.initializeAndFinishSwap(
          ethAddr,
          steAddr,
          codeHash,
          pubKey,
          signedState,
        ),
      );
    } catch (err) {
      console.error("Error", err);
      return false;
    }
    this.initialized = true;
    return this.initialized;
  }

  /**
   * Starts a user-triggered recovery session for the channel(s) a past session
   * left behind. The backend rebuilds the identity behind `l2Address` — the
   * address this browser remembers for that session — from its cached key, for
   * this connection only, and then reports the stranded channel it finds; the
   * "Recover Funding" notice completes the force-close. Mirrors finishSwap
   * without needing the signed state: the backend still holds the record.
   */
  public async recoverStranded(l2Address: string): Promise<boolean> {
    if (this.initialized) {
      return Promise.reject("client is already initialized");
    }

    await this.conn.connect();
    try {
      let ethAddr = Address.zero();
      let steAddr = "";
      let pubKey = "";
      let codeHash = "0x";
      if (this.ethProvider) {
        ethAddr = Address.fromJSON(this.ethProvider.getAddress());
      }
      if (this.ethAddress) {
        ethAddr = this.ethAddress;
      }
      if (this.steProvider) {
        steAddr = this.steProvider.getAddress();
      }
      if (this.steAddress) {
        steAddr = this.steAddress;
      }
      if (this.ckbProvider && this.nervosPubKey) {
        pubKey = this.nervosPubKey;
        codeHash = await this.ckbProvider.getCodeHash();
      }
      console.log("Recover with: ", ethAddr, steAddr, codeHash, l2Address);
      const initialized = await promiseWithTimeout(
        finishSwapTimeout,
        this.conn.initializeRecovery(ethAddr, steAddr, codeHash, pubKey, l2Address),
      );
      if (initialized?.l2Address) {
        this.l2Address = initialized.l2Address;
      }
    } catch (err) {
      console.error("Error", err);
      return false;
    }
    this.initialized = true;
    return this.initialized;
  }

  public shutdownClient() {
    if (this.initialized) {
      this.conn.request(new Dispose(this.defaultAddress!));
      disconnect(this.conn);
      this.initialized = false;
    }
  }

  public async getChainID(): Promise<bigint[]> {
    const chainIDs: bigint[] = [];
    if (this.ethProvider) {
      chainIDs.push(await this.ethProvider.getChainID());
    }
    if (this.steProvider) {
      chainIDs.push(await this.steProvider.getChainID());
    }
    if (this.ckbProvider) {
      chainIDs.push(await this.ckbProvider.getChainID());
    }
    return chainIDs;
  }

  public async getChains(): Promise<GetChainsResponse> {
    return this.conn.request(new GetChains()).then((resp) => {
      return resp as GetChainsResponse;
    });
  }

  /**
   * Queries the backend for assets on chains with the matching chain ID.
   *
   * @param chainIDQuery The type of chain IDs that should be used for the
   * query.
   * Either "custom" for defining a list of IDs by "ID" manually,
   * "own" referring to the ID of the currently connected chain,
   * or "all" for asking for all assets on all chains.
   * @returns
   */
  public async getAssets(
    chainIDQuery: ChainIDQuery,
  ): Promise<GetAssetsResponse> {
    let chainIDs = Array<bigint>();
    switch (chainIDQuery.type) {
      case "custom":
        chainIDs = chainIDQuery.IDs;
        break;
      case "own":
        chainIDs = await this.getChainID();
        break;
      case "all":
        (await this.getChains()).chains.map((c) => chainIDs.push(c.ID));
        break;
    }
    console.log("Assets: ", chainIDs);
    return this.conn.request(new GetAssets(chainIDs)).then((resp) => {
      return resp as GetAssetsResponse;
    });
  }

  public async getDecimals(asset: ChannelAsset): Promise<number | undefined> {
    console.log("getting decimals");
    console.log("Asset: ", asset);
    return this.conn
      .request(new GetDecimals(asset))
      .then((resp) => {
        return (resp as GetDecimalsResponse).decimals;
      })
      .catch((err) => {
        if (err instanceof PerunError) {
          console.error(`getDecimals: ${err.error}`);
        } else {
          console.error(`getDecimals: ${err}`);
        }
        return undefined;
      });
  }

  public async getQuote(
    fromAsset: ChannelAsset,
    toAsset: ChannelAsset,
    hubAddress: string,
  ): Promise<GetQuoteResponse | undefined> {
    console.log("getting quote, Address: ", hubAddress, fromAsset, toAsset);
    return this.conn
      .request(new GetQuote(fromAsset, toAsset, hubAddress))
      .then((resp) => {
        return resp as GetQuoteResponse;
      })
      .catch((err) => {
        if (err instanceof PerunError) {
          console.error(`getQuote: ${err.error}`);
        } else {
          console.error(`getQuote: ${err}`);
        }
        return undefined;
      });
  }

  public async getBalance(
    Asset: ChannelStellarAsset | ChannelNervosAsset,
  ): Promise<GetBalanceResponse | undefined> {
    return this.conn
      .request(new GetBalance(Asset))
      .then((resp) => {
        console.log("getBalance response: ", resp);
        return resp as GetBalanceResponse;
      })
      .catch((err) => {
        if (err instanceof PerunError) {
          console.error(`getBalance: ${err.error}`);
        } else {
          console.error(`getBalance: ${err}`);
        }
        return undefined;
      });
  }

  public async getGuardrails(
    assets: Asset[],
    hubAddress: string,
  ): Promise<GetGuardrailsResponse | undefined> {
    return this.conn
      .request(new GetGuardrails(assets, hubAddress))
      .then((resp) => {
        return resp as GetGuardrailsResponse;
      })
      .catch((err) => {
        if (err instanceof PerunError) {
          console.error(`getGuardrails: ${err.error}`);
        } else {
          console.error(`getGuardrails: ${err}`);
        }
        return undefined;
      });
  }

  public async getTimeout(
    timeoutType: TimeoutType,
  ): Promise<number | undefined> {
    return this.conn
      .request(new GetTimeout(timeoutType))
      .then((resp) => {
        return (resp as GetTimeoutResponse).timeout;
      })
      .catch((err) => {
        if (err instanceof PerunError) {
          console.error(`getTimeout: ${err.error}`);
        } else {
          console.error(`getTimeout: ${err}`);
        }
        return undefined;
      });
  }

  public async getSignedState(channelID: ChannelID): Promise<SignedState> {
    return this.conn.request(new GetSignedState(channelID)).then((resp) => {
      console.log("getSignedState response: ", resp);
      return resp as SignedState;
    });
  }

  public async sendSignedState(
    signedState: SignedState,
  ): Promise<Success | Error> {
    return this.conn
      .request(
        new SignedState(
          signedState.params,
          signedState.state,
          signedState.sigs,
        ),
      )
      .then((resp) => {
        return resp as Success | Error;
      });
  }

  public async initiateSwap(swap: Swap) {
    console.log("Initializing swap", swap.ID);
    this.state.addSwap(swap);
    let backends = swap.backends;
    let myBalances = swap.myBalances;
    let peerBalances = swap.peerBalances;
    let channelAssets = swap.channelAssets;
    if (swap.channelAssets[0] instanceof ChannelNervosAsset) {
      if (
        swap.channelAssets[0].address.codeHash ===
        "0x0000000000000000000000000000000000000000000000000000000000000000"
      ) {
        console.log(
          "Swapping CKB for sUDT",
          swap.channelAssets[0].address.codeHash,
          myBalances,
          peerBalances,
        );
        myBalances[0] += 8000000000n;
        peerBalances[0] += 8000000000n;
      } else {
        // Swapping sUDT
        if (
          (swap.channelAssets[1] instanceof ChannelNervosAsset &&
            swap.channelAssets[1].address.codeHash !==
              "0x0000000000000000000000000000000000000000000000000000000000000000") ||
          !(swap.channelAssets[1] instanceof ChannelNervosAsset)
        ) {
          console.log("Adding CKB as third asset");
          // Swapping sUDT for sUDT or sUDT for other Asset
          channelAssets = [
            swap.channelAssets[0],
            swap.channelAssets[1],
            new ChannelNervosAsset(
              new SUDT(
                0,
                "0x0000000000000000000000000000000000000000000000000000000000000000",
                "",
                "",
              ),
              BigInt(swap.fromAsset.chainID),
            ),
          ];
          myBalances = [myBalances[0], myBalances[1], 8000000000n];
          peerBalances = [peerBalances[0], peerBalances[1], 8000000000n];
          backends = [backends[0], backends[1], 3];
        } else {
          // Swapping sUDT for CKB
          console.log(
            "Swapping sUDT for CKB",
            swap.channelAssets[0].address.codeHash,
            myBalances,
            peerBalances,
          );
          myBalances[1] += 8000000000n;
          peerBalances[1] += 8000000000n;
        }
      }
    } else if (swap.channelAssets[1] instanceof ChannelNervosAsset) {
      if (
        swap.channelAssets[1].address.codeHash ==
        "0x0000000000000000000000000000000000000000000000000000000000000000"
      ) {
        myBalances[1] += 8000000000n;
        peerBalances[1] += 8000000000n;
      } else {
        channelAssets = [
          swap.channelAssets[0],
          swap.channelAssets[1],
          new ChannelNervosAsset(
            new SUDT(
              0,
              "0x0000000000000000000000000000000000000000000000000000000000000000",
              "",
              "",
            ),
            BigInt(swap.toAsset.chainID),
          ),
        ];
        myBalances = [myBalances[0], myBalances[1], 8000000000n];
        peerBalances = [peerBalances[0], peerBalances[1], 8000000000n];
        backends = [backends[0], backends[1], 3];
      }
    }
    const chState = new ChannelState(
      channelAssets,
      backends,
      myBalances,
      peerBalances,
    );
    const prop = new Proposal(
      swap.ID,
      swap.peerAddressEth,
      swap.peerAddressCC,
      chState,
    );

    const submit = async () => {
      console.log("requesting the peer as the adjudicator transaction sender");
      await this.setAdjTxSender(swap).catch((err) => console.error(err));

      console.log(`sending channel proposal ${swap.ID}`);
      await this.openChannel(prop)
        .then((resp) => {
          if (resp instanceof Success) {
            console.log("channel created");
            swap.status = "channelCreated";
            this.state.emitUpdateSwap(swap.ID);
          } else {
            swap.status = "proposalRejected";
            swap.rejectionReason = resp.reason;
            this.state.emitUpdateSwap(swap.ID);
          }
        })
        .catch((err) => {
          swap.error = err;
          this.state.emitUpdateSwap(swap.ID);
        });
    };

    const cancel = () => {
      swap.error = new Error(i18n.t("errorCanceledSwapInit"));
      this.state.emitUpdateSwap(swap.ID);
    };

    dispatchPerunNotice(
      initiateSwapNotice(
        swap.fromAsset.name,
        swap.fromAsset.type,
        submit,
        cancel,
      ),
    );
  }

  private async saveSignedState(
    swap: Swap,
    update: boolean,
    channelID: ChannelID,
  ) {
    if (channelID !== swap.channelID) {
      console.log(
        "channelID does not match swap's channelID",
        channelID,
        swap.channelID,
        update,
      );
      throw new Error("cannot perform swap with non-underlying channel");
    }
    let signedState = new SignedState(
      new Params([], 0, [], "", "", "", true, false),
      new SState([], 0, "", "", new Allocation([], [], [], []), "", false),
      [],
    );
    await this.getSignedState(channelID).then((res) => {
      if (res !== undefined) {
        console.log("signed state retrieved", res);
        signedState = res!;
        localStorage.setItem("signedState", JSON.stringify(signedState));
      } else {
        signedState = new SignedState(
          new Params([], 0, [], "", "", "", true, false),
          new SState(
            [],
            0,
            "",
            "NoApp",
            new Allocation([], [], [], []),
            "",
            false,
          ),
          [],
        );
      }
    });
    localStorage.setItem("signedstate", JSON.stringify(signedState));
    //update controls the submit function, if saveSignedState was called after updateChannel we do not want to call performSwap again.
    if (update) {
      console.log("dispatching finalizing swap notice");
      dispatchPerunNotice(finalizingSwapNotice(JSON.stringify(signedState)));
    } else {
      console.log("dispatching performing swap notice");
      dispatchPerunNotice(performingSwapNotice(JSON.stringify(signedState)));
    }
  }

  //send back signed state, needs SendSignedState message in ws backend
  public async sendChannelSignedState(state: SignedState) {
    try {
      await this.sendSignedState(state).then((res) => {
        if (res instanceof Success) {
          return "success";
        } else {
          return "error";
        }
      });
    } catch (err) {
      return "error";
    }
  }

  public recoverFunds(signedState: SignedState) {
    return this.conn.request(new RecoverFunds(signedState)).catch((err) => {
      console.log("recoverFunds error: ", err);
      throw Error(err);
    });
  }

  public recoverFunding(channelID: string) {
    return this.conn.request(new RecoverFunding(channelID)).catch((err) => {
      console.log("recoverFunding error: ", err);
      throw Error(err);
    });
  }

  private performSwap(swap: Swap) {
    console.log(`performing swap ${swap.ID}`);
    if (!swap.channelID) {
      throw new Error("cannot perform swap with non-underlying channel");
    }
    let myBalances = swap.myBalances;
    let peerBalances = swap.peerBalances;
    let backends = swap.backends;
    // TODO: Change this once we add ethereum
    let channelAssets = swap.channelAssets;
    if (swap.channelAssets[0] instanceof ChannelNervosAsset) {
      if (
        swap.channelAssets[0].address.codeHash ===
        "0x0000000000000000000000000000000000000000000000000000000000000000"
      ) {
        console.log(
          "Swapping CKB for sUDT",
          swap.channelAssets[0].address.codeHash,
          myBalances,
          peerBalances,
        );
        myBalances[0] += 8000000000n;
        peerBalances[0] += 8000000000n;
      } else {
        // Swapping sUDT
        if (
          (swap.channelAssets[1] instanceof ChannelNervosAsset &&
            swap.channelAssets[1].address.codeHash !==
              "0x0000000000000000000000000000000000000000000000000000000000000000") ||
          !(swap.channelAssets[1] instanceof ChannelNervosAsset)
        ) {
          console.log("Adding CKB as third asset");
          // Swapping sUDT for sUDT or sUDT for other Asset
          channelAssets = [
            swap.channelAssets[0],
            swap.channelAssets[1],
            new ChannelNervosAsset(
              new SUDT(
                0,
                "0x0000000000000000000000000000000000000000000000000000000000000000",
                "",
                "",
              ),
              BigInt(swap.fromAsset.chainID),
            ),
          ];
          myBalances = [myBalances[0], myBalances[1], 8000000000n];
          peerBalances = [peerBalances[0], peerBalances[1], 8000000000n];
          backends = [backends[0], backends[1], 3];
        } else {
          // Swapping sUDT for CKB
          console.log(
            "Swapping sUDT for CKB",
            swap.channelAssets[0].address.codeHash,
            myBalances,
            peerBalances,
          );
          myBalances[1] += 8000000000n;
          peerBalances[1] += 8000000000n;
        }
      }
    } else if (swap.channelAssets[1] instanceof ChannelNervosAsset) {
      if (
        swap.channelAssets[1].address.codeHash ==
        "0x0000000000000000000000000000000000000000000000000000000000000000"
      ) {
        myBalances[1] += 8000000000n;
        peerBalances[1] += 8000000000n;
      } else {
        channelAssets = [
          swap.channelAssets[0],
          swap.channelAssets[1],
          new ChannelNervosAsset(
            new SUDT(
              0,
              "0x0000000000000000000000000000000000000000000000000000000000000000",
              "",
              "",
            ),
            BigInt(swap.toAsset.chainID),
          ),
        ];
        myBalances = [myBalances[0], myBalances[1], 8000000000n];
        peerBalances = [peerBalances[0], peerBalances[1], 8000000000n];
        backends = [backends[0], backends[1], 3];
      }
    }
    // The proposed new state corresponds to swapping the balances and
    // setting it as final.
    const newState = new ChannelState(
      channelAssets,
      backends,
      peerBalances,
      myBalances,
      true,
    );
    this.sendUpdate(swap.channelID!, newState, swap.ID).then((res) => {
      if (res instanceof Success) {
        console.log(`swap update complete ${swap.ID}`);
        swap.status = "closingChannel";
        this.state.emitUpdateSwap(swap.ID);
        return this.finalizeSwap(swap);
      } else {
        swap.status = "updateRejected";
        swap.rejectionReason = res.reason;
        this.state.emitUpdateSwap(swap.ID);
      }
    });
  }

  private finalizeSwap(swap: Swap) {
    if (!swap.channelID) {
      throw new Error("cannot finalize swap with non-underlying channel");
    }

    const channel = this.state.getChannel(swap.channelID!)!;
    if (!channel) {
      throw new Error("finalize swap: channel not found");
    }
    if (!channel.state.isFinal) {
      throw new Error("finalize swap expects the channel state to be final");
    }

    dispatchPerunNotice(finalizingSwapNotice());
    this.closeChannel(channel.id, "default", swap.withdrawalAddress)
      .then((res) => {
        if (res instanceof Success) {
          console.log(`swap is finalized ${swap.ID}`);
          swap.status = "complete";
          this.state.emitUpdateSwap(swap.ID);
          revertPerunNotice();
        } else {
          // We can't get a Rejected Message since the channel's state was final.
          swap.error = new Error("closing has failed due to unknown reasons");
          throw new Error(`finalizeSwap: expected Success, got ${res}`);
        }
      })
      .catch((err) => {
        swap.error = err;
      });
  }

  /**
   * Sets the adjudicator transaction sender for a swap.
   * This is especially necessary if we perform a cross-chain swap where the
   * user cannot send transactions on other chains that the connected one.
   * If setting the sender fails, it updates the swap status.
   *
   * @param swap
   * @throws - Error if it could not set the adjudicator Tx sender.
   */
  private async setAdjTxSender(swap: Swap) {
    // setAdjTxSender calls the client's setAdjTxSender method and on error,
    // updates the swap status and throws an error.
    console.log("setting adj tx sender", swap.assets[1]);

    console.log(swap.assets);
    const setAdjTxSender = async (chainID: bigint) => {
      const resp = await this.conn
        .request(new SetAdjTxSender(chainID, swap.peerAddressEth))
        .catch((err: string) => {
          console.error(err);
          return new PerunError(err);
        });
      if (resp instanceof PerunError) {
        swap.status = "noAdjTxSender";
        this.state.emitUpdateSwap(swap.ID);
        throw Error("could not set the adjudicator Tx sender");
      }
    };

    try {
      if (
        swap.fromAsset instanceof EthereumAsset &&
        swap.peerAddressEth !== Address.zero()
      ) {
        await setAdjTxSender(swap.fromAsset.chainID);
      }
      // If we do a cross-chain swap, we need to set the adjudicator Tx sender
      // for both chains.
      if (
        swap.fromAsset.chainID !== swap.toAsset.chainID &&
        swap.toAsset instanceof EthereumAsset
      ) {
        await setAdjTxSender(swap.toAsset.chainID);
      }
    } catch (e) {
      console.error(e);
      return;
    }
  }

  /**
   * Sends a channel proposal to the given peer address.
   *
   * @param proposal -
   * @returns A promise for the success or rejection of the proposal.
   */
  private openChannel(proposal: Proposal): Promise<Success | Rejected> {
    console.log("sending channel proposal", proposal.state);
    const openMsg = new OpenChannel(
      proposal.id,
      proposal.peerAddressEth,
      proposal.peerAddressCC,
      BigInt(challengeDurationSeconds),
      proposal.state,
    );

    return this.conn.request(openMsg).catch((err) => {
      const reason = getRejectReason(err, "channel proposal");
      if (reason) {
        return reason;
      } else {
        return { reason: err };
      }
    });
  }

  private sendUpdate(
    channelID: ChannelID,
    newState: ChannelState,
    swapID: SwapID,
  ): Promise<Success | Rejected> {
    const channel = this.state.getChannel(channelID);
    if (!channel) {
      throw new Error(`sendUpdate: unknown channel ${channelID}`);
    }
    return this.conn
      .request(new UpdateChannel(channelID, newState))
      .then((msg) => {
        // Update the state of the channel.
        channel!.updateState(newState);
        this.saveSignedState(this.state.getSwap(swapID)!, true, channelID).then(
          (r) => console.log(r),
        );
        // Fixme: Check if necessary.
        this.state.updateChannel(channel!);
        revertPerunNotice();
        return msg as Success;
      })
      .catch((err) => {
        const reason = getRejectReason(err, "channel update");
        if (reason) {
          return reason;
        } else {
          throw new Error(err);
        }
      });
  }

  public async forceCloseSwap(swapID: SwapID) {
    const swap = this.state.getSwap(swapID);
    if (!swap) {
      throw Error("swap not found");
    }
    if (!swap.channelID) {
      throw Error("swap has no underlying channel yet");
    }

    swap.status = "forceClosing";
    this.state.emitUpdateSwap(swap.ID);

    await this.closeChannel(swap.channelID, "force")
      .then(() => {
        swap.status = "forceClosed";
        this.state.emitUpdateSwap(swap.ID);
      })
      .catch((err) => {
        console.error(`force closing channel: ${err}`);
        swap.status = "failedClosing";
        swap.error = err;
        this.state.emitUpdateSwap(swap.ID);
      });
  }

  /**
   * Sends a close channel request for the channel with the given channel ID.
   *
   * @param ChID
   * @param type - The closing type, either default or by forcing the channel
   * closing (registers a non-final state).
   * @param withdrawalAddress - The address to which the funds should be
   * withdrawn. If undefined, uses the client's address.
   * @returns - A promise for the success or rejection of the channel closing
   * request.
   */
  private closeChannel(
    ChID: ChannelID,
    type: "default" | "force" = "default",
    withdrawalAddress?: string,
  ): Promise<Success | Rejected> {
    return this.conn
      .request(new CloseChannel(ChID, type === "force", withdrawalAddress))
      .catch((err) => {
        const reason = getRejectReason(err, "channel update");
        if (reason) {
          return reason;
        } else {
          throw Error(err);
        }
      });
  }

  /**
   * Sends the given transaction using the Web3Provider and returns the
   * transaction response.
   * @param transaction The TransactionRequest which should have set the chainID.
   * @returns A promise for the TransactionResponse.
   * @protected
   */
  private sendTransaction(
    transaction: TransactionRequest,
  ): Promise<TransactionResponse> {
    return this.ethProvider!.sendTransaction(transaction);
  }

  private initHandlers() {
    // Handle new blocks and check if the balance changed.
    if (this.ethProvider) {
      this.ethProvider!.onBlock((blockNumber) => {
        this.ethProvider!.getETHBalance(blockNumber).then((newBal) => {
          const oldBal = this.state.getBalance();
          if (!oldBal || oldBal.valueOf().toString() !== newBal) {
            this.state.updateBalance(BigInt(newBal));
          }
        });
      });
    }

    // Set the proposal handler.
    this.conn.onRequest("channelProposal", (prop) => {
      console.log("Received channel proposal");
      return Promise.resolve(
        new ProposalResponse(
          false,
          "X-Chain clients do not support incoming channel proposals",
        ),
      );
    });

    // Set the handler for channelCreated events which creates a new channel
    // object and adds it to the client's state channels list.
    this.conn.onChannelEvent("channelCreated", async (msg) => {
      const chID = msg.id;

      console.log(`Channel created ${chID} for ${msg.proposalID}`);
      const chInfo = (await this.conn.request(
        new GetChannelInfo(chID),
      )) as ChannelInfo;

      const channel = new Channel(
        chID,
        chInfo.peerAddressEth,
        chInfo.peerAddressCC,
        chInfo.state,
      );

      this.state.addChannel(channel);

      const swap = this.state.getSwap(msg.proposalID);
      if (!swap) {
        console.error(`Created channel for unknown proposal ${msg.proposalID}`);
        return;
      }
      swap.channelID = chID;
      // On reload the swap is restored with status "pendingProposal" because
      // the openChannel() callback (which sets "channelCreated") was lost.
      // Advance the status here so performSwap can legally transition to
      // "closingChannel".
      if (swap.status === "pendingProposal") {
        swap.status = "channelCreated";
      }
      this.saveSignedState(swap, false, chID).then((r) => console.log(r));
      console.log("update swap");
      this.state.emitUpdateSwap(swap.ID);

      this.performSwap(swap);
    });

    // The handler for channelClosed events which removes the closed channel
    // from the channels list.
    this.conn.onChannelEvent("channelClosed", async (msg) => {
      console.log(`channel closed ${msg.id}`);
      // A swap whose channel is closed is over, whatever its own close request
      // returned. That request fails when the peer closed the channel
      // cooperatively first, which used to leave the page on "Completing swap"
      // and offer a second signature for a channel that no longer exists
      // (staging, 2026-09-17).
      const swap = this.state.getSwapByChannel(msg.id);
      if (swap && swap.status === "closingChannel") {
        swap.status = "complete";
        this.state.emitUpdateSwap(swap.ID);
        revertPerunNotice();
      }
      if (this.state.getChannel(msg.id)) {
        this.state.removeChannel(msg.id);
      }
    });

    this.conn.onChannelEvent("fundingError", async (msg) => {
      console.error(`received funding error: ${msg.error}`);
      const swap = this.state.getSwap(msg.proposalID);
      if (!swap) {
        console.error(`Created channel for unknown proposal ${msg.proposalID}`);
        return;
      }
      swap.channelID = msg.channelID;
      swap.status = "fundingFailed";
      swap.error = new Error(msg.error);
      this.state.emitUpdateSwap(swap.ID);
    });

    // Handle OpenChannelFound: notifies the user and offers to close the channel.
    this.conn.onChannelEvent(
      "openChannelFound",
      async (msg: OpenChannelFound) => {
        console.log("OpenChannelFound received", msg);
        let signedStates: string[] = [];
        for (const state of msg.signedStates) {
          signedStates.push(JSON.stringify(state));
        }

        dispatchPerunNotice(
          openChannelFoundNotice(signedStates, async (idx: number) => {
            // Store signed state so FinishSwap can pre-fill it.
            localStorage.setItem("signedState", signedStates[idx]);

            // Disconnect without sending Dispose: Dispose deletes the l2cache file
            // which FinishSwap needs to recover the l2 key. Just close the connection
            // so the backend keeps the session alive for FinishSwap to attach to.
            this.shutdownClient();
            dispatchNavigateToFinish();
          }),
        );
      },
    );

    // Handle FundingChannelFound: notifies the user and offers to recover deposited funds.
    this.conn.onChannelEvent(
      "fundingChannelFound",
      async (msg: FundingChannelFound) => {
        console.log("FundingChannelFound received", msg);

        dispatchPerunNotice(
          fundingChannelFoundNotice(msg.channelIDs, async (idx: number) => {
            const channelID = msg.channelIDs[idx];
            console.log("Recovering funding for channel", channelID);
            try {
              dispatchPerunNotice(recoverFundingRunningNotice());
              await this.recoverFunding(channelID);
              dispatchPerunNotice(recoveredFundsNotice());
            } catch (err) {
              console.error("recoverFunding failed:", err);
              dispatchPerunNotice(recoverFundingFailedNotice(err));
            }
          }),
        );
      },
    );

    // Sets the update handler which sets the update proposal and a proposal
    // responder to the corresponding channel.
    this.conn.onRequest("channelUpdate", (msg) => {
      // TODO: Maybe at least accept final states with balance staying the same.
      return Promise.resolve(
        new ProposalResponse(
          false,
          "X-Chain clients do not support incoming channel updates",
        ),
      );
    });

    try {
      // Set the handler for sendTx requests.
      this.conn.onRequest("sendSteTx", (msg: SendSteTx) => {
        return txMutex.runExclusive(async () => {
          console.log("Processing sendSteTx...");
          const result = await this.handleSendSteTx(msg);

          // Introduce a buffer before unlocking the mutex
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
          return result;
        });
      });
      this.conn.onRequest("sendETHTx", (msg: SendETHTx) => {
        return txMutex.runExclusive(async () => {
          console.log("Processing sendETHTx...");
          const result = await this.handleSendETHTx(msg);

          // Introduce a buffer before unlocking the mutex
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
          return result;
        });
      });
      this.conn.onRequest("sendNervTx", (msg: SendNervTx) => {
        return txMutex.runExclusive(async () => {
          console.log("Processing sendNervTx...");
          const result = await this.handleSendNervTx(msg);

          // Introduce a buffer before unlocking the mutex
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
          return result;
        });
      });
      this.conn.onRequest("signNervData", (msg: SignNervData) => {
        return txMutex.runExclusive(async () => {
          console.log("Processing signNervData...");
          const result = await this.handleSignNervData(msg);

          // Introduce a buffer before unlocking the mutex
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
          return result;
        });
      });
    } catch (err: any) {
      console.log(err);
    }
  }

  /**
   * Handles SignETHData requests by responding with a corresponding signature.
   *
   * @param msg - The SignETHData message containing the data to be signed.
   * @returns
   * @private
   */
  /*private async handleSignSteData(
    msg: SignSteData
  ): Promise<SignResponse | PerunError> {
    console.log("handleSignData");
    // const buf = Buffer.from(msg.data, "base64");
    // const hash = utils.keccak256(buf);
    let sig: string;
    try {
      console.log("signing stellar", msg);
      sig = await this.signMessage(msg.data);
    } catch (err) {
      console.error("signing failed", err);
      throw Error("handleSignData failed");
    }
    revertPerunNotice();
    return new SignResponse(utils.arrayify(sig));
  }*/

  /**
   * Handles SendETHTx requests by sending the transaction and responding
   * with the signature of the transaction response.
   *
   * @param msg - The SendETHTx message containing the transaction.
   * @returns
   * @private
   */
  private async handleSendETHTx(
    msg: SendETHTx,
  ): Promise<SendETHTxResponse | PerunError> {
    try {
      console.log("received SendETHTx");
      console.log(msg.transaction);

      const IDs = await this.getChainID();
      if (!IDs.includes(msg.chainID)) {
        console.error(`received Tx for a different chain ID ${msg.chainID}.`);
        return new PerunError("handleSendEthTX failed");
      }

      const txResponse = await this.sendTransaction(
        msg.transaction as TransactionRequest,
      );

      console.log("Sent eth transaction");
      console.log("eth transaction:", txResponse);

      revertPerunNotice();
      return new SendETHTxResponse(txResponse);
    } catch (err) {
      console.error("Error in sending transaction:", err);
      return new PerunError("handleSendEthTX failed");
    }
  }

  private async handleSendSteTx(
    msg: SendSteTx,
  ): Promise<SendSteTxResponse | PerunError> {
    try {
      const txResponse = await this.steProvider!.sendTransaction(
        msg.transaction,
      );
      console.log("Sent ste transaction");
      revertPerunNotice();
      return new SendSteTxResponse(txResponse);
    } catch (err) {
      console.error("Error in sending transaction:", err);
      return new PerunError("handleSendSteTX failed");
    }
  }

  private async handleSendNervTx(
    msg: SendNervTx,
  ): Promise<SendNervTxResponse | PerunError> {
    console.log("handleSendNervTx", msg);
    try {
      // The CCC signer signs whichever inputs belong to the wallet account
      // that is selected RIGHT NOW; the transaction's inputs belong to the
      // account this session was registered with. Nothing downstream checks
      // that these are the same account: on a mismatch CCC finds no input of
      // "its own", silently signs nothing, and the backend submits the
      // unsigned transaction, which the chain rejects with OmniLock error 8
      // (mainnet 2026-08-31 close, 2026-09-01 funding). Refuse loudly instead.
      const sessionAccount = await this.accountMismatch();
      if (sessionAccount) {
        return new PerunError(sessionAccount);
      }

      const txResponse = await this.ckbProvider!.signTransaction(
        msg.transaction,
      );
      revertPerunNotice();
      console.log("nervos transaction:", txResponse);

      // Second line of defense: if signing changed nothing, no signature was
      // produced — returning these witnesses would submit an unsigned
      // transaction as if it were signed.
      if (
        !witnessesChanged(
          msg.transaction.tx_view.witnesses,
          txResponse.witnesses,
        )
      ) {
        console.error(
          "handleSendNervTx: signer returned the transaction unsigned",
          msg.transaction.tx_view.witnesses,
          txResponse.witnesses,
        );
        return new PerunError(
          "wallet produced no signature for this transaction — is the right account selected?",
        );
      }

      return new SendNervTxResponse(txResponse.witnesses);
    } catch (err) {
      console.error("Error in sending transaction:", err);
      return new PerunError("handleSendNervTx failed");
    }
  }

  /**
   * Returns an error message when the wallet's currently selected account is
   * not the account this session was registered with, and undefined when they
   * match (or when the check cannot be made, to avoid breaking flows that
   * never had the problem).
   */
  private async accountMismatch(): Promise<string | undefined> {
    try {
      const registered = this.ethProvider?.getAddress();
      const active = await this.ckbProvider?.getInternalAddress();
      if (
        registered &&
        active &&
        active.toLowerCase() !== registered.toLowerCase()
      ) {
        console.error(
          `wallet account mismatch: session belongs to ${registered}, wallet has ${active} selected`,
        );
        return `wrong wallet account selected: this session belongs to ${registered}, but the wallet is on ${active} — select the original account or reconnect`;
      }
    } catch (err) {
      console.warn("could not verify wallet account:", err);
    }
    return undefined;
  }

  private async handleSignNervData(
    msg: SignNervData,
  ): Promise<SignResponse | PerunError> {
    const mismatch = await this.accountMismatch();
    if (mismatch) {
      return new PerunError(mismatch);
    }
    const buf = base64ToUint8Array(msg.data);
    const hash = utils.keccak256(buf);
    let sig: string;
    try {
      console.log("signing", hash);
      sig = await this.ckbProvider!.signMessage(hash);
      console.log("raw signature: ", sig);
      sig = await convertEthSignatureToDER(sig);
      console.log("converted signature: ", sig);
    } catch (err) {
      console.error("Error in sending transaction:", err);
      return new PerunError("handleSendSteTX failed");
    }
    return new SignResponse(utils.arrayify(sig));
  }
}

/**
 * Reports whether signing actually changed any witness. Witness arrays arrive
 * as hex strings with or without a 0x prefix, and the signer may pad the array
 * with empty entries — padding alone is not a signature.
 */
export function witnessesChanged(
  before: readonly string[],
  after: readonly string[],
): boolean {
  const norm = (w: string | undefined) => {
    const v = (w ?? "").toLowerCase();
    return v.startsWith("0x") ? v.slice(2) : v;
  };
  const len = Math.max(before.length, after.length);
  for (let i = 0; i < len; i++) {
    if (norm(after[i]) !== norm(before[i])) return true;
  }
  return false;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export type ChainIDQuery =
  | {
      type: "custom";
      IDs: bigint[];
    }
  | { type: "own" }
  | { type: "all" };
